import { X, Mail, MessageSquare, AlertCircle } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useModalFocus } from '../hooks/useModalFocus';

// `invitedCount` / `inviteCapacity` count SEATS THE OWNER CAN FILL, so both exclude the owner:
// a cap of 4 people means 3 invitable seats. `invitedCount` includes pending invitations,
// because a seat is committed when the invite is sent, not when it is accepted.
export function InviteFamilyDrawer({ isOpen, onClose, onInvite, onOpen, invitedCount = 0, inviteCapacity = 0, pendingCount = 0, ownerFirstName = '' }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  // Anything the server rejects. Rendered at the top of the drawer: this used to be set on the
  // parent screen, which sits BEHIND the open drawer, so the owner never saw why a send failed.
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const errorRef = useRef(null);

  const isFull = invitedCount >= inviteCapacity;

  // Refresh the seat count every time the drawer opens — members and invites can have changed
  // since the Family screen last loaded — and clear anything left over from a previous attempt.
  useEffect(() => {
    if (!isOpen) return;
    setSubmitError('');
    setEmailError('');
    setFirstNameError('');
    if (typeof onOpen === 'function') onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Bring the message into view rather than leaving it above the fold on a small screen.
  useEffect(() => {
    if (submitError && errorRef.current) {
      errorRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [submitError]);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleInvite = async () => {
    setSubmitError('');

    // Validation
    if (!firstName.trim()) {
      setFirstNameError('Please enter their first name');
      return;
    }

    if (!email.trim()) {
      setEmailError('Please enter an email address');
      return;
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (isFull) {
      setSubmitError(
        `Your household is full — all ${inviteCapacity} seats are taken${pendingCount ? `, including ${pendingCount} pending invitation${pendingCount === 1 ? '' : 's'}` : ''}. Remove a member or cancel a pending invitation before inviting someone else.`
      );
      return;
    }

    // The server is the authority — it re-checks seats and rejects duplicates. Await it so a
    // failure can be surfaced here instead of on the screen behind this drawer.
    setSubmitting(true);
    try {
      await onInvite(email, firstName, message);
      setFirstName('');
      setEmail('');
      setMessage('');
      setEmailError('');
      setFirstNameError('');
      setSubmitError('');
    } catch (err) {
      setSubmitError(err?.message || 'Could not send the invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailError('');
    setSubmitError('');
  };

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
    setFirstNameError('');
  };

  const panelRef = useRef(null);
  const titleId = useId();
  useModalFocus({ isOpen, panelRef, onClose, canClose: !submitting });

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-[448px] mx-auto"
      >
        <div className="px-6 py-6">
          {/* Handle bar */}
          <div className="flex justify-center mb-4">
            <div className="w-10 h-1 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 id={titleId} className="text-xl">Invite Family Member</h2>
            <button
              type="button"
              onClick={onClose}
              className="w-11 h-11 -mr-2 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" aria-hidden="true" />
            </button>
          </div>

          {/* Server / seat-limit errors — inside the drawer, above the form */}
          {submitError && (
            <div
              ref={errorRef}
              role="alert"
              aria-live="assertive"
              className="mb-5 flex gap-3 rounded-2xl border border-[#E4B9B9] bg-[#FBEFEF] px-4 py-3"
            >
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#A33A3A]" aria-hidden="true" />
              <p className="text-sm text-[#7A2E2E]">{submitError}</p>
            </div>
          )}

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label htmlFor="invite-first-name" className="block text-sm font-medium text-gray-900 mb-2">
                First name
              </label>
              <input
                id="invite-first-name"
                type="text"
                value={firstName}
                onChange={handleFirstNameChange}
                placeholder="Mike"
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-2xl"
              />
              {firstNameError && (
                <p className="text-sm text-[#B85A5A] mt-2">{firstNameError}</p>
              )}
            </div>

            {/* Email Input */}
            <div>
              <label htmlFor="invite-email" className="block text-sm font-medium text-gray-900 mb-2">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#757575]" />
                <input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="mike@email.com"
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl"
                />
              </div>
              {emailError && (
                <p className="text-sm text-[#B85A5A] mt-2">{emailError}</p>
              )}
            </div>

            {/* Personal Message */}
            <div>
              <label htmlFor="invite-message" className="block text-sm font-medium text-gray-900 mb-2">
                Personal message <span className="text-[#757575] font-normal">(optional)</span>
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-[#757575]" />
                <textarea
                  id="invite-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Hi ${firstName.trim() || 'there'}, ${ownerFirstName || 'I'} would love for you to join ${ownerFirstName ? `${ownerFirstName}'s` : 'our'} household on MealPlan Genie.`}
                  rows={3}
                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-2xl resize-none"
                />
              </div>
            </div>

            {/* Permissions Info */}
            <div className="bg-[#F0F3EC] rounded-2xl p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">
                They'll be able to:
              </p>
              <ul className="space-y-1.5 text-sm text-[#757575]">
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>View and edit shared meal plans</span>
                </li>
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>View and edit shopping lists</span>
                </li>
              
                <li className="flex gap-2">
                  <span>✓</span>
                  <span>See what's planned for the week</span>
                </li>
              </ul>
            </div>

            {/* Member Count Info */}
            <p className="text-xs text-center text-[#757575]">
              {invitedCount} of {inviteCapacity} household members invited
              {pendingCount > 0 && ` (${pendingCount} pending)`}
            </p>
          </div>

          {/* Actions */}
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-[48px] py-3 bg-white text-[#5F6B3F] font-medium border-2 border-[#5F6B3F] rounded-full hover:bg-[#F0F3EC] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleInvite}
              disabled={submitting}
              className="flex-1 min-h-[48px] py-3 bg-[#AFBB8B] text-[#1F2814] font-medium rounded-full hover:bg-[#9AA876] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Sending…' : 'Send Invitation'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
